import React, { useState, useEffect, useCallback } from 'react';
import texasApi from '../../services/texasApi';
import { landlordApi } from '../../services/api';

export const HVACReadinessPage: React.FC = () => {
  const [summary, setSummary] = useState<any>(null);
  const [properties, setProperties] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sumRes, propRes] = await Promise.allSettled([
        texasApi.hvacReadiness.getPortfolioSummary(),
        landlordApi.get('api/properties'),
      ]);
      if (sumRes.status === 'fulfilled') setSummary(sumRes.value || null);
      if (propRes.status === 'fulfilled') {
        const d = propRes.value?.data;
        let list: any[] = [];
        if (Array.isArray(d)) list = d;
        else if (Array.isArray(d?.data)) list = d.data;
        else if (Array.isArray(d?.properties)) list = d.properties;
        setProperties(list);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load HVAC readiness');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const readyCount = summary?.readyCount ?? 0;
  const atRiskCount = summary?.atRiskCount ?? 0;
  const propertyCount = summary?.propertyCount ?? properties.length;

  const StatusBadge = ({ ready }: { ready: boolean }) =>
    ready ? (
      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200">
        <i className="fas fa-check mr-1"></i>Ready
      </span>
    ) : (
      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
        <i className="fas fa-exclamation-triangle mr-1"></i>At Risk
      </span>
    );

  const handleRecordSummerTest = async (propertyId: string) => {
    try {
      await texasApi.hvacReadiness.recordSummerTest(propertyId);
      loadData();
    } catch (err: any) {
      setError(err?.message || 'Failed to record summer test');
    }
  };

  const handleRecordWinterChecklist = async (propertyId: string) => {
    try {
      await texasApi.hvacReadiness.recordWinterChecklist(propertyId);
      loadData();
    } catch (err: any) {
      setError(err?.message || 'Failed to record winter checklist');
    }
  };

  return (
    <div data-testid="hvac-readiness-page" className="texas-page-container">
      <h1>HVAC Readiness</h1>
      <p className="page-description">
        Texas freeze/winter preparation — portfolio HVAC checks.
      </p>

      {!loading && (
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="text-xs font-bold text-slate-500 tracking-wider">PROPERTIES</div>
            <div className="text-3xl font-black text-slate-900 mt-1">{propertyCount ?? '—'}</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="text-xs font-bold text-emerald-600 tracking-wider">READY</div>
            <div className="text-3xl font-black text-emerald-600 mt-1">{readyCount}</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="text-xs font-bold text-red-600 tracking-wider">AT RISK</div>
            <div className="text-3xl font-black text-red-600 mt-1">{atRiskCount}</div>
          </div>
        </section>
      )}

      {error && <div className="error" data-testid="error">{error}</div>}

      <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <h2>Property Readiness</h2>
        {properties.length === 0 ? (
          <div className="no-results">No properties on file</div>
        ) : (
          <div className="space-y-2.5">
            {properties.map((p: any) => {
              const name = p.name || p.propertyName || 'Untitled Property';
              return (
                <div key={p.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200/80 flex items-center justify-between">
                  <div>
                    <div className="font-bold text-gray-900 text-xs">{name}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleRecordSummerTest(p.id)}
                      className="px-3 py-1 bg-sky-100 text-sky-700 rounded text-xs hover:bg-sky-200"
                      data-testid={`summer-test-${p.id}`}
                    >
                      Summer Test
                    </button>
                    <button
                      onClick={() => handleRecordWinterChecklist(p.id)}
                      className="px-3 py-1 bg-amber-100 text-amber-700 rounded text-xs hover:bg-amber-200"
                      data-testid={`winter-checklist-${p.id}`}
                    >
                      Winter Checklist
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default HVACReadinessPage;
